import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';

const MissionPage = () => {
  return (
    <main className="pt-16 md:pt-20 bg-[#FAFAF9]" data-testid="mission-page" style={{ fontFamily: "'Open Sans', sans-serif" }}>
      <Helmet>
        <title>Our Mission — Built for solo operators — Servitium</title>
        <meta name="description" content="Servitium is the digital infrastructure for 4.25 million solo and micro-businesses in Canada. Our vision: every solo operator competes on quality, not price." />
        <meta property="og:title" content="Our Mission — Built for solo operators — Servitium" />
        <meta property="og:description" content="Servitium is the digital infrastructure for 4.25 million solo and micro-businesses in Canada. Our vision: every solo operator competes on quality, not price." />
        <meta property="og:url" content="https://servitium.ca/mission" />
        <meta name="twitter:title" content="Our Mission — Built for solo operators — Servitium" />
        <meta name="twitter:description" content="Servitium is the digital infrastructure for 4.25 million solo and micro-businesses in Canada. Our vision: every solo operator competes on quality, not price." />
        <meta property="og:image" content="https://servitium.ca/og-preview.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Servitium — Infrastructure for solo and micro-businesses" />
        <meta name="twitter:image" content="https://servitium.ca/og-preview.png" />
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 md:py-24" data-testid="mission-hero">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold tracking-[0.22em] uppercase text-[#880808] mb-4">
            OUR MISSION
          </p>
          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-[#36454F] tracking-tight leading-[1.1] mb-5"
            data-testid="mission-headline"
          >
            EVERY SOLO OPERATOR SHOULD COMPETE ON QUALITY, NOT PRICE.
          </h1>
          <p className="text-base md:text-lg text-[#57534E] leading-relaxed max-w-2xl mx-auto" style={{ fontWeight: 400 }}>
            4.25 million solo and micro-businesses run Canada's economy — 455,500 in British Columbia alone. We're building the infrastructure they deserve.
          </p>
        </div>
      </section>

      {/* Two Cards — OUR MISSION + WHY WE EXIST */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {/* Our Mission */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.05)] border border-[#E7E5E4]" data-testid="mission-card">
              <div className="h-[4px] bg-[#880808]" />
              <div className="p-6 md:p-8">
                <p className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-[#880808] mb-3">OUR MISSION</p>
                <h3 className="text-xl md:text-2xl font-extrabold text-[#36454F] leading-tight mb-3">
                  DIGITAL INFRASTRUCTURE FOR SOLO OPERATORS TO GROW PROFESSIONALLY.
                </h3>
                <p className="text-sm md:text-base text-[#6b7280] leading-relaxed">
                  Solo and micro-businesses run on a patchwork of texts, cash apps, and verbal handshakes. We built Servitium so they can run on something better — a system that verifies their identity, organizes their workflow, processes their payments, and remembers every job they complete.
                </p>
              </div>
            </div>

            {/* Our Vision */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.05)] border border-[#E7E5E4]" data-testid="why-card">
              <div className="h-[4px] bg-[#880808]" />
              <div className="p-6 md:p-8">
                <p className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-[#D4AF37] mb-3">OUR VISION</p>
                <h3 className="text-xl md:text-2xl font-extrabold text-[#36454F] leading-tight mb-3">
                  EVERY SOLO OPERATOR COMPETES ON QUALITY, NOT PRICE.
                </h3>
                <p className="text-sm md:text-base text-[#6b7280] leading-relaxed">
                  The current model rewards whoever shouts loudest — not whoever delivers best. Servitium changes that by making verified track records the basis for trust. When customers can see who actually does the work, great work earns the work.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Core Values */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-[#880808] mb-3">OUR CORE VALUES</p>
            <p className="text-base md:text-lg text-[#6b7280] italic" style={{ fontWeight: 400 }}>
              The foundation of trust and craftsmanship.
            </p>
          </div>

          {/* Values panel with light tinted background */}
          <div className="bg-[#F5F0E8]/40 rounded-3xl p-6 md:p-10">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {[
                { title: 'RESPECT THE CRAFT', body: 'Your work deserves to be taken seriously. We build accordingly.' },
                { title: 'CLARITY OVER CHAOS', body: 'One system. One source of truth.' },
                { title: 'PROOF BEATS PROMISES', body: 'History compounds. Promises don\'t.' },
                { title: 'BUILT FOR HUMANS', body: 'Designed for operators, not algorithms.' },
                { title: 'PROTECT TRUST', body: 'Trust is infrastructure. We don\'t shortcut it.' },
                { title: 'RECORDS ARE PORTABLE', body: 'Your reputation belongs to you, not the platform.' },
              ].map((v, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl overflow-hidden shadow-[0_2px_10px_rgba(0,0,0,0.04)] border border-[#E7E5E4] text-center"
                  data-testid={`mission-value-${i}`}
                >
                  <div className="h-[3px] bg-[#880808]" />
                  <div className="p-5 md:p-6">
                    <h4 className="text-sm font-extrabold text-[#36454F] uppercase tracking-wide mb-1.5">{v.title}</h4>
                    <p className="text-xs text-[#6b7280]">{v.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Statement Card — What We're Building */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.05)] border border-[#E7E5E4]" data-testid="building-card">
            <div className="h-[4px] bg-[#880808]" />
            <div className="p-8 md:p-12 text-center">
              <p className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-[#880808] mb-4">WHAT WE'RE BUILDING</p>
              <h3 className="text-xl md:text-2xl lg:text-3xl font-extrabold text-[#36454F] leading-tight mb-4 max-w-3xl mx-auto">
                WE'RE NOT BUILDING A MARKETPLACE. WE'RE BUILDING THE INFRASTRUCTURE SOLO OPERATORS RUN ON.
              </h3>
              <p className="text-base md:text-lg text-[#6b7280] leading-relaxed max-w-3xl mx-auto" style={{ fontWeight: 400 }}>
                A marketplace decides who customers see. Infrastructure stays out of the way and lets the work speak. Servitium verifies your identity, runs your workflow, processes your payments, and builds a record that follows you wherever your business goes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20" data-testid="mission-cta">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-2xl md:text-3xl font-extrabold text-[#36454F] tracking-tight leading-tight mb-6"
            data-testid="mission-cta-headline"
          >
            BUILD A RECORD ONLY YOU CAN BUILD.
          </h2>
          <div className="flex flex-col items-center gap-4">
            <Link to="/get" data-testid="mission-cta-button">
              <Button className="bg-[#880808] hover:bg-[#6b0606] text-white rounded-full px-10 py-4 text-base font-bold shadow-md hover:shadow-lg transition-all duration-200">
                Join free
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <a href="#" aria-label="Download on the App Store">
                <img src="/apple-badge.svg" alt="App Store" className="h-10 opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
              </a>
              <a href="#" aria-label="Get it on Google Play">
                <img src="/google-badge.svg" alt="Google Play" className="h-10 opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
              </a>
            </div>
            <p className="text-sm text-[#6b7280]">
              Free across the Lower Mainland · $25/month elsewhere
            </p>
          </div>
        </div>
      </section>

      {/* Footer spacer */}
      <div className="pb-8" />
    </main>
  );
};

export default MissionPage;
